import { MutableRefObject, useEffect, useRef } from "react";
import L from "leaflet";
import { TruckingGuestRoutePointDto } from "../dtos/ShipmentTruckingGuestInfoResponseDto";
import { safeCoordTuple } from "../utils/safeCoordTuple";
import { isValidCoordinates } from "../utils/isValidCoordinates";
import { CommonMessage } from "../../../base/components/message/CommonMessage";

interface UseAllRouteMarkersProps {
  mapInst: MutableRefObject<L.Map | null>;
  mapReady: boolean;
  isMountedRef: MutableRefObject<boolean>;
  routePoints: TruckingGuestRoutePointDto[];
  currentStep: number;
  accentColor: string;
}

type PointState = "done" | "arrived" | "current" | "upcoming";

const COLORS: Record<PointState, { fill: string; stroke: string; text: string }> = {
  done: { fill: "#14a047", stroke: "#14a047", text: "#fff" }, // Xanh lá cây - Đã check-out
  arrived: { fill: "#3b82f6", stroke: "#1e40af", text: "#fff" }, // Xanh dương - Đã check-in
  current: { fill: "#ffffff", stroke: "#ffffff", text: "black" }, // Trắng - Đang đi tới
  upcoming: { fill: "#ef4444", stroke: "#b91c1c", text: "#fff" }, // Đỏ - Điểm tiếp theo
};

const makeMarkerIcon = (index: number, state: PointState, stopTypeName: string): L.DivIcon => {
  const c = COLORS[state];
  const size = state === "current" ? 36 : 28;
  const anchor = state === "current" ? 18 : 14;

  const pulse =
    state === "current"
      ? `<div style="position:absolute;inset:-6px;border-radius:50%;border:2px solid ${c.fill};opacity:0.45;animation:_rp_pulse 1.8s ease-out infinite;"></div>`
      : "";

  const badge = `
    <div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${c.fill};border:2.5px solid ${c.stroke};
      display:flex;align-items:center;justify-content:center;
      font-size:${state === "current" ? 14 : 11}px;font-weight:700;color:${c.text};
      box-shadow:0 2px 8px rgba(0,0,0,${state === "current" ? 0.35 : 0.18});
      position:relative;z-index:1;
    ">${index + 1}</div>`;

  const label = `
    <div class="rp-label" style="
      position:absolute;bottom:${size + 4}px;left:50%;transform:translateX(-50%);
      background:rgba(30,30,30,0.82);color:#fff;font-size:10px;white-space:nowrap;
      padding:2px 6px;border-radius:4px;pointer-events:none;
      opacity:${state === "current" ? 1 : 0};transition:opacity .2s;
      z-index:10;max-width:160px;overflow:hidden;text-overflow:ellipsis;
    ">${stopTypeName}</div>`;

  return L.divIcon({
    className: "rp-marker-wrap",
    html: `
      <style>
        @keyframes _rp_pulse {
          0%   { transform:scale(1);   opacity:0.45; }
          70%  { transform:scale(1.9); opacity:0.12; }
          100% { transform:scale(2.2); opacity:0;    }
        }
        .rp-marker-wrap:hover .rp-label { opacity:1 !important; }
      </style>
      <div style="position:relative;width:${size}px;height:${size}px;">
        ${pulse}${badge}${label}
      </div>`,
    iconSize: [size, size],
    iconAnchor: [anchor, anchor],
    popupAnchor: [0, -(anchor + 6)],
  });
};

export const useAllRouteMarkers = ({ mapInst, mapReady, isMountedRef, routePoints, currentStep, accentColor }: UseAllRouteMarkersProps): void => {
  const markersRef = useRef<L.Marker[]>([]);

  useEffect(() => {
    if (!mapInst.current || !mapReady || !isMountedRef.current) return;
    const map = mapInst.current;

    markersRef.current.forEach((m) => {
      try {
        map.removeLayer(m);
      } catch {}
    });
    markersRef.current = [];

    routePoints.forEach((point, idx) => {
      const lat = point.targetLat;
      const lng = point.targetLng;
      if (lat == null || lng == null || !isValidCoordinates(lat, lng)) return;

      const tuple = safeCoordTuple(lat, lng);
      if (!tuple) return;

     const state: PointState = point.status >= 2 ? "done" : point.status === 1 ? "arrived" : idx === currentStep ? "current" : "upcoming";

     try {
       const marker = L.marker(tuple, {
         icon: makeMarkerIcon(idx, state, point.stopTypeName ?? `Điểm ${idx + 1}`),
         zIndexOffset: state === "current" ? 1000 : state === "arrived" ? 750 : state === "upcoming" ? 500 : 0,
       })
         .addTo(map)
         .bindPopup(
           `<b>${idx + 1}. ${point.stopTypeName ?? ""}</b><br/>
       <span style="font-size: 12px; color: #6b7280;">${point.checkInAddress ?? ""}</span>
       ${state === "done" ? "<br/><span style='color:#10b981; margin-top: 4px; display: block;'>✓ Đã hoàn thành</span>" : ""}
       ${state === "arrived" ? "<br/><span style='color:#3b82f6; font-weight:600; margin-top: 4px; display: block;'>Đã đến điểm (Check-in)</span>" : ""}
       ${state === "current" ? "<br/><span style='color:#374151; font-weight:600; margin-top: 4px; display: block;'>Đang di chuyển đến</span>" : ""}`,
           { closeButton: false, maxWidth: 240 },
         );

       if (state === "current" || state === "arrived") {
         marker.openPopup();
       }

       markersRef.current.push(marker);
     } catch (err: any) {
        CommonMessage.error(`route-point marker error: ${err.response.data.message}`);
     }
    });

    return () => {
      markersRef.current.forEach((m) => {
        try {
          map.removeLayer(m);
        } catch {}
      });
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePoints, currentStep, mapReady, accentColor]);
};
